import { useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import { useIslandHost } from "@frontend/contexts";

import { Toggle } from "./Toggle";
import { type ToggleDataProps, toggleDataSchema } from "./Toggle.schema";
import { TOGGLE_TAG_NAME } from "./constants";

export interface ToggleHandle {
  pressed: (value?: boolean) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TOGGLE_TAG_NAME]: { props: ToggleDataProps; handle: ToggleHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-toggle": HTMLElement & ToggleHandle;
  }
}

export function RWCToggle({
  ref,
  pressed: initialPressed,
  disabled: initialDisabled,
  icon,
  label,
  ...props
}: ToggleDataProps & { ref?: Ref<ToggleHandle> }) {
  const host = useIslandHost();
  const [pressed, setPressed] = useState(initialPressed ?? false);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useImperativeHandle(
    ref,
    () => ({
      pressed: (value = true) => setPressed(value),
      disabled: (value = true) => setDisabled(value),
    }),
    [],
  );

  // The attribute is set before the event so a listener reads the new state
  function handlePressedChange(next: boolean) {
    setPressed(next);
    host?.toggleAttribute("pressed", next);
    host?.dispatchEvent(
      new CustomEvent("toggle:change", {
        detail: { pressed: next },
        bubbles: true,
      }),
    );
  }

  return (
    <Toggle
      {...props}
      pressed={pressed}
      onPressedChange={handlePressedChange}
      disabled={disabled}
    >
      {icon ? <Icon name={icon as IconName} /> : null}
      {label}
    </Toggle>
  );
}

componentManager.register(
  rwc(TOGGLE_TAG_NAME, {
    component: RWCToggle,
    dataSchema: toggleDataSchema,
  }),
);
